import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { Gift, ArrowRight, CheckCircle2, AlertTriangle, Loader2, LogIn, Clock } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function formatExpiry(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleDateString(undefined, {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return null;
  }
}

/**
 * /redeem?code=XXXX — QR/link landing page.
 *
 * Behavior:
 *   - Auto-extracts ?code= from the URL.
 *   - If not logged in → redirect to /login?return=/redeem?code=XXXX (code preserved
 *     in sessionStorage as a belt-and-suspenders fallback).
 *   - Otherwise auto-redeem the code on mount.
 *   - Success state: "Access unlocked" + expiry (trial) or new tier (upgrade).
 */
export default function Redeem() {
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();
  const codeFromUrl = (params.get('code') || '').trim();

  const [loading, setLoading] = useState(true);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('session_token');
    if (!codeFromUrl) {
      setLoading(false);
      setError('no_code');
      return;
    }
    if (!token) {
      // Preserve the code so even after login the /redeem page still knows it.
      try { sessionStorage.setItem('pending_redeem_code', codeFromUrl); } catch {
        // session storage might be blocked (private mode); URL param still carries it.
      }
      navigate(`/login?return=${encodeURIComponent(`${location.pathname}${location.search}`)}`, { replace: true });
      return;
    }
    (async () => {
      try {
        const res = await axios.post(
          `${API}/gift-codes/redeem`,
          { code: codeFromUrl },
          { headers: { Authorization: `Bearer ${token}` } },
        );
        setResult(res.data);
        try { sessionStorage.removeItem('pending_redeem_code'); } catch {
          // ignore — sessionStorage unavailable
        }
      } catch (err) {
        const status = err?.response?.status;
        const detail = err?.response?.data?.detail;
        if (status === 401) {
          navigate(`/login?return=${encodeURIComponent(`${location.pathname}${location.search}`)}`, { replace: true });
          return;
        }
        setError(detail || 'Could not redeem this code.');
      } finally {
        setLoading(false);
      }
    })();
  }, [codeFromUrl, navigate, location.pathname, location.search]);

  const expiryLabel = result?.expires_at ? formatExpiry(result.expires_at) : null;

  return (
    <div className="min-h-screen bg-[#050505] text-[#EAE7E1]" data-testid="redeem-page">
      <header className="border-b border-[#1A1A1A]">
        <div className="max-w-[520px] mx-auto px-5 sm:px-6 py-5 flex items-center justify-between">
          <Link to="/" className="text-sm tracking-[0.4em] font-bold text-[#D4AF37] uppercase" data-testid="redeem-home-link">VelvetGate</Link>
          <Link to="/" className="text-xs text-[#777] hover:text-[#EAE7E1] transition-colors">← Home</Link>
        </div>
      </header>

      <main className="max-w-[520px] mx-auto px-5 sm:px-6 py-10 sm:py-16">
        <div className="text-[10px] tracking-[0.35em] text-[#D4AF37] uppercase font-semibold mb-3" data-testid="redeem-eyebrow">
          Gift Code
        </div>
        <h1 className="text-3xl sm:text-4xl font-black tracking-tight text-[#F3EEE7] mb-3" data-testid="redeem-title">
          Redeem Your Access
        </h1>
        <p className="text-sm sm:text-base text-[#B9B1A6] leading-relaxed mb-8" data-testid="redeem-description">
          One-time redemption. Your VelvetGate account will unlock the features attached to this code.
        </p>

        {loading && (
          <div className="flex flex-col items-center text-center py-12" data-testid="redeem-loading">
            <Loader2 className="w-6 h-6 text-[#D4AF37] animate-spin mb-3" />
            <p className="text-sm text-[#B9B1A6]">Redeeming your code&hellip;</p>
          </div>
        )}

        {!loading && error && error !== 'no_code' && (
          <div
            className="border border-[#C44545]/25 bg-[#C44545]/5 rounded-sm p-5 flex items-start gap-3"
            data-testid="redeem-error-state"
          >
            <AlertTriangle className="w-5 h-5 text-[#C44545] flex-shrink-0 mt-0.5" />
            <div className="min-w-0">
              <div className="text-base font-bold text-[#F3EEE7] mb-1">Could not redeem</div>
              <p className="text-sm text-[#B9B1A6] leading-relaxed" data-testid="redeem-error-message">{error}</p>
              <div className="mt-3 flex flex-wrap gap-2">
                <Link
                  to="/"
                  className="inline-flex items-center justify-center gap-2 border border-[#1A1A1A] text-[#EAE7E1] hover:border-[#D4AF37]/40 hover:bg-[#0D0D0D] px-3 py-2 rounded-sm font-semibold text-xs transition-colors"
                  data-testid="redeem-home-cta"
                >
                  Return Home
                </Link>
              </div>
            </div>
          </div>
        )}

        {!loading && error === 'no_code' && (
          <div className="border border-[#D4AF37]/25 rounded-sm bg-gradient-to-br from-[#0F0D08] to-[#050505] p-5" data-testid="redeem-no-code-state">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-[#D4AF37]/10 flex items-center justify-center flex-shrink-0">
                <LogIn className="w-5 h-5 text-[#D4AF37]" />
              </div>
              <div className="min-w-0">
                <div className="text-base font-bold text-[#F3EEE7] mb-1">No code detected</div>
                <p className="text-sm text-[#B9B1A6]">Open the full redemption link or scan the QR again.</p>
              </div>
            </div>
          </div>
        )}

        {!loading && result && (
          <div
            className="border border-emerald-500/25 rounded-sm bg-gradient-to-br from-[#082018] to-[#050505] p-6"
            data-testid="redeem-success-state"
          >
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-emerald-500/10 flex items-center justify-center flex-shrink-0">
                <CheckCircle2 className="w-5 h-5 text-emerald-400" />
              </div>
              <div className="min-w-0">
                <div className="text-lg font-bold text-[#F3EEE7] mb-1" data-testid="redeem-success-heading">
                  Access unlocked
                </div>
                <p className="text-sm text-[#B9B1A6] leading-relaxed" data-testid="redeem-success-summary">
                  {result.type === 'trial'
                    ? 'Your trial is active — all premium features are now available on your account.'
                    : `Your account has been upgraded to ${result.membership_tier?.toUpperCase() || 'PRO'}.`}
                </p>
                {result.type === 'trial' && expiryLabel && (
                  <div className="mt-3 inline-flex items-center gap-2 text-xs text-[#D4AF37]" data-testid="redeem-trial-expiry">
                    <Clock className="w-3.5 h-3.5" />
                    <span>Expires {expiryLabel}</span>
                  </div>
                )}
                {result.type === 'upgrade' && (
                  <div className="mt-3 inline-flex items-center gap-2 text-xs text-[#D4AF37]" data-testid="redeem-upgrade-tier">
                    <Gift className="w-3.5 h-3.5" />
                    <span>Tier: {(result.membership_tier || '').toUpperCase()}</span>
                  </div>
                )}
                <div className="mt-5 flex flex-wrap gap-2">
                  <Link
                    to="/dashboard"
                    className="inline-flex items-center justify-center gap-2 bg-gradient-to-r from-[#8C6B1F] via-[#D4AF37] to-[#F2D27A] hover:opacity-90 text-[#050505] px-4 py-2.5 rounded-sm font-bold text-sm transition-opacity"
                    data-testid="redeem-dashboard-cta"
                  >
                    Go to Dashboard <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        )}

        {!loading && !error && !result && !codeFromUrl && null}

        <div className="mt-10 pt-6 border-t border-[#1A1A1A] flex flex-wrap gap-x-5 gap-y-2 text-xs text-[#777]">
          <Link to="/guide"   className="hover:text-[#D4AF37] transition-colors" data-testid="redeem-footer-guide">Plans &amp; Access</Link>
          <Link to="/faq"     className="hover:text-[#D4AF37] transition-colors">FAQ</Link>
          <Link to="/contact" className="hover:text-[#D4AF37] transition-colors">Contact</Link>
          <span className="ml-auto text-[#444]">© VelvetGate</span>
        </div>
      </main>
    </div>
  );
}
